import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Paper,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Home, Add, Delete, Save } from '@mui/icons-material';
import {
  createProduct,
  CreateProductRequest,
  updateProduct,
  UpdateProductRequest,
  getProductById,
} from '../../services/product-service';
import BackButton from '../../components/BackButton/BackButton';
import styles from './ProductFormPage.module.css';
import { Product } from '../../types';

interface ImageFormData {
  image_url: string;
  alt_text: string;
  is_primary: boolean;
  display_order: number;
}

interface SpecificationFormData {
  key: string;
  value: string;
}

interface FormData {
  sku: string;
  name: string;
  description: string;
  price: string;
  is_active: boolean;
  manual: string;
  cisp1_commission: string;
  reseller_commission: string;
  use_acer_empresas_price_rule: boolean;
  acer_empresas_net_price: string;
  category: string;
  images: ImageFormData[];
  specifications: SpecificationFormData[];
}

const ProductFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const isEditMode = !!id;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    sku: '',
    name: '',
    description: '',
    price: '',
    is_active: true,
    manual: '',
    cisp1_commission: '',
    reseller_commission: '',
    use_acer_empresas_price_rule: false,
    acer_empresas_net_price: '',
    category: '',
    images: [],
    specifications: [],
  });

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Loading and alert states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  // Store original product data for updates
  const [originalProduct, setOriginalProduct] = useState<Product | null>(null);

  const showAlert = (message: string, severity: 'success' | 'error') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const getAcerEmpresasNetPrice = (product: Product): number => {
    const specifications = product.specifications || {};
    const overrides = specifications.acer_price_overrides || {};
    const priceList = specifications.acer_price_list || {};
    const value =
      product.acer_empresas_net_price ??
      overrides.acer_empresas_net_price ??
      priceList.acer_empresas_net_price ??
      0;

    return Number(value) || 0;
  };

  const getUseAcerEmpresasPriceRule = (product: Product): boolean => {
    const specifications = product.specifications || {};
    const overrides = specifications.acer_price_overrides || {};
    return product.use_acer_empresas_price_rule ?? overrides.use_acer_empresas_price_rule ?? false;
  };

  const formatSpecificationValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const parseSpecificationValue = (value: string): any => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';

    if (
      (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) ||
      (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'))
    ) {
      try {
        return JSON.parse(trimmedValue);
      } catch (error) {
        return value;
      }
    }

    return value;
  };

  // Load product data for edit mode
  useEffect(() => {
    const loadProductData = async () => {
      if (!isEditMode || !id) return;

      setIsLoadingProduct(true);
      try {
        const product = await getProductById(id);

        if (product) {
          // Store original product data
          setOriginalProduct(product);

          // Format monetary values for display
          const formatValueForDisplay = (value: number): string => {
            return value.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          };

          setFormData({
            sku: product.sku || '',
            name: product.name || '',
            description: product.description || '',
            price: formatValueForDisplay(product.price || 0),
            is_active: product.is_active ?? true,
            manual: product.manual || '',
            cisp1_commission: formatValueForDisplay(product.cisp1_commission || 0),
            reseller_commission: formatValueForDisplay(product.reseller_commission || 0),
            use_acer_empresas_price_rule: getUseAcerEmpresasPriceRule(product),
            acer_empresas_net_price: formatValueForDisplay(getAcerEmpresasNetPrice(product)),
            category: product.category || '',
            images: (product.images || []).map(img => ({
              image_url: img.image_url || '',
              alt_text: img.alt_text || '',
              is_primary: img.is_primary || false,
              display_order: img.display_order || 0,
            })),
            specifications: product.specifications
              ? Object.entries(product.specifications).map(([key, value]) => ({
                  key,
                  value: formatSpecificationValue(value),
                }))
              : [],
          });
        } else {
          showAlert('Erro ao consultar produto', 'error');
        }
      } catch (error) {
        console.error('Error loading product:', error);
        showAlert('Erro ao consultar produto', 'error');
      } finally {
        setIsLoadingProduct(false);
      }
    };

    loadProductData();
  }, [id, isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
        { label: 'Produtos', path: '/admin/products' },
        { label: 'Editar Produto' },
      ]);
    } else {
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
        { label: 'Produtos', path: '/admin/products' },
        { label: 'Novo Produto' },
      ]);
    }

    return () => {
      setBreadcrumbs([{ label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> }]);
    };
  }, [isEditMode, setBreadcrumbs]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when field is edited
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Format monetary values (R$ 1.234,56)
  const formatMonetaryValue = (value: string): string => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');

    if (!numericValue) return '';

    // Convert to number and format with thousands separator and decimal places
    const numberValue = parseInt(numericValue) / 100;
    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Format decimal values with 2 decimal places (123,45)
  const formatDecimalValue = (value: string): string => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');

    if (!numericValue) return '';

    // Convert to number and format with decimal places
    const numberValue = parseInt(numericValue) / 100;
    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Parse formatted value back to plain number string
  const parseFormattedValue = (formattedValue: string): string => {
    if (!formattedValue) return '';

    // Remove currency symbols, spaces, and convert comma to dot
    const cleanValue = formattedValue.replace(/[^\d,]/g, '').replace(',', '.');

    return cleanValue;
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Apply monetary mask for price fields
    if (name === 'price' || name === 'acer_empresas_net_price') {
      const formattedValue = formatMonetaryValue(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    }

    // Clear error when field is edited
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCommissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Apply decimal mask for commission fields
    const formattedValue = formatDecimalValue(value);
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue,
    }));

    // Clear error when field is edited
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const addNewImage = () => {
    setFormData(prev => ({
      ...prev,
      images: [
        ...prev.images,
        {
          image_url: '',
          alt_text: '',
          is_primary: prev.images.length === 0, // First image is primary by default
          display_order: prev.images.length,
        },
      ],
    }));
  };

  const handleImageChange = (
    index: number,
    field: keyof ImageFormData,
    value: string | boolean | number
  ) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    }));
  };

  const handleImageDelete = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images
        .filter((_, i) => i !== index)
        .map((img, i) => ({
          ...img,
          display_order: i,
        })),
    }));
  };

  const addNewSpecification = () => {
    setFormData(prev => ({
      ...prev,
      specifications: [
        ...prev.specifications,
        {
          key: '',
          value: '',
        },
      ],
    }));
  };

  const handleSpecificationChange = (
    index: number,
    field: keyof SpecificationFormData,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      specifications: prev.specifications.map((spec, i) =>
        i === index ? { ...spec, [field]: value } : spec
      ),
    }));
  };

  const handleSpecificationDelete = (index: number) => {
    setFormData(prev => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU é obrigatório';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (!formData.price || formData.price.trim() === '') {
      newErrors.price = 'Preço é obrigatório';
    } else {
      const parsedPrice = parseFormattedValue(formData.price);
      if (isNaN(Number(parsedPrice)) || Number(parsedPrice) <= 0) {
        newErrors.price = 'Preço deve ser um número válido maior que zero';
      }
    }

    if (formData.acer_empresas_net_price.trim() !== '') {
      const parsedAcerNetPrice = parseFormattedValue(formData.acer_empresas_net_price);
      if (isNaN(Number(parsedAcerNetPrice)) || Number(parsedAcerNetPrice) < 0) {
        newErrors.acer_empresas_net_price = 'Preço Net Acer deve ser um número válido';
      }
    }

    if (!formData.cisp1_commission || formData.cisp1_commission.trim() === '') {
      newErrors.cisp1_commission = 'Comissão CISP1 é obrigatória';
    } else {
      const parsedCommission = parseFormattedValue(formData.cisp1_commission);
      if (isNaN(Number(parsedCommission)) || Number(parsedCommission) < 0) {
        newErrors.cisp1_commission = 'Comissão CISP1 deve ser um número válido';
      }
    }

    if (!formData.reseller_commission || formData.reseller_commission.trim() === '') {
      newErrors.reseller_commission = 'Comissão do Revendedor é obrigatória';
    } else {
      const parsedCommission = parseFormattedValue(formData.reseller_commission);
      if (isNaN(Number(parsedCommission)) || Number(parsedCommission) < 0) {
        newErrors.reseller_commission = 'Comissão do Revendedor deve ser um número válido';
      }
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Categoria é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && originalProduct) {
        // Update existing product
        const updateData: UpdateProductRequest = {
          id: originalProduct.id,
          sku: formData.sku,
          name: formData.name,
          description: formData.description,
          price: Number(parseFormattedValue(formData.price)),
          is_active: formData.is_active,
          manual: formData.manual,
          cisp1_commission: Number(parseFormattedValue(formData.cisp1_commission)),
          reseller_commission: Number(parseFormattedValue(formData.reseller_commission)),
          use_acer_empresas_price_rule: formData.use_acer_empresas_price_rule,
          acer_empresas_net_price: Number(
            parseFormattedValue(formData.acer_empresas_net_price) || 0
          ),
          category: formData.category,
          images: formData.images
            .filter(img => img.image_url.trim() !== '')
            .map((img, index) => {
              // Find corresponding original image to preserve ID
              const originalImage = originalProduct.images?.find(
                origImg => origImg.image_url === img.image_url
              );

              return {
                ...(originalImage?.id && { id: originalImage.id }),
                product_id: originalProduct.id,
                image_url: img.image_url,
                alt_text: img.alt_text,
                is_primary: img.is_primary,
                display_order: img.display_order,
              };
            }),
          specifications: formData.specifications
            .filter(spec => spec.key.trim() !== '')
            .reduce(
              (acc, spec) => {
                acc[spec.key] = parseSpecificationValue(spec.value);
                return acc;
              },
              {} as Record<string, any>
            ),
        };

        const response = await updateProduct(originalProduct.id, updateData);

        if (response && response.id) {
          showAlert('Produto atualizado com sucesso', 'success');
          // Redirect to the product details page
          setTimeout(() => {
            navigate(`/admin/products/${response.id}`);
          }, 1500);
        }
      } else {
        // Create new product
        const productData: CreateProductRequest = {
          sku: formData.sku,
          name: formData.name,
          description: formData.description,
          price: Number(parseFormattedValue(formData.price)),
          is_active: formData.is_active,
          manual: formData.manual,
          cisp1_commission: Number(parseFormattedValue(formData.cisp1_commission)),
          reseller_commission: Number(parseFormattedValue(formData.reseller_commission)),
          use_acer_empresas_price_rule: formData.use_acer_empresas_price_rule,
          acer_empresas_net_price: Number(
            parseFormattedValue(formData.acer_empresas_net_price) || 0
          ),
          category: formData.category,
          images: formData.images.filter(img => img.image_url.trim() !== ''),
          specifications: formData.specifications
            .filter(spec => spec.key.trim() !== '')
            .reduce(
              (acc, spec) => {
                acc[spec.key] = parseSpecificationValue(spec.value);
                return acc;
              },
              {} as Record<string, any>
            ),
        };

        const response = await createProduct(productData);

        if (response && response.id) {
          showAlert('Produto cadastrado com sucesso', 'success');
          // Redirect to the new product details page
          setTimeout(() => {
            navigate(`/admin/products/${response.id}`);
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Error saving product:', error);
      if (isEditMode) {
        showAlert('Erro ao atualizar produto', 'error');
      } else {
        showAlert('Erro ao cadastrar produto', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  // Show loading when fetching product data
  if (isEditMode && isLoadingProduct) {
    return (
      <Container maxWidth="lg" className={styles.container}>
        <BackButton />
        <Paper className={styles.paper}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Carregando dados do produto...
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={styles.container}>
      <BackButton />

      <Paper className={styles.paper}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Editar Produto' : 'Novo Produto'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                error={!!errors.sku}
                helperText={errors.sku}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Produto"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                error={!!errors.description}
                helperText={errors.description}
                multiline
                rows={4}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Preço Net CISP1"
                name="price"
                value={formData.price}
                onChange={handlePriceChange}
                error={!!errors.price}
                helperText={errors.price}
                placeholder="0,00"
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Preço Net Acer Empresas"
                name="acer_empresas_net_price"
                value={formData.acer_empresas_net_price}
                onChange={handlePriceChange}
                error={!!errors.acer_empresas_net_price}
                helperText={errors.acer_empresas_net_price}
                placeholder="0,00"
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.use_acer_empresas_price_rule}
                    onChange={handleInputChange}
                    name="use_acer_empresas_price_rule"
                    color="primary"
                  />
                }
                label="Aplicar regra Acer Empresas < CISP1"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Comissão CISP1"
                name="cisp1_commission"
                value={formData.cisp1_commission}
                onChange={handleCommissionChange}
                error={!!errors.cisp1_commission}
                helperText={errors.cisp1_commission}
                placeholder="0,00"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Comissão do Revendedor"
                name="reseller_commission"
                value={formData.reseller_commission}
                onChange={handleCommissionChange}
                error={!!errors.reseller_commission}
                helperText={errors.reseller_commission}
                placeholder="0,00"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Categoria"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                error={!!errors.category}
                helperText={errors.category}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manual"
                name="manual"
                value={formData.manual}
                onChange={handleInputChange}
                placeholder="URL do manual do produto"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    name="is_active"
                    color="primary"
                  />
                }
                label="Produto Ativo"
              />
            </Grid>

            {/* Images Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="h2">
                  Imagens do Produto
                </Typography>
                <Button variant="outlined" startIcon={<Add />} onClick={addNewImage} size="small">
                  Adicionar Imagem
                </Button>
              </Box>

              {formData.images.map((image, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="URL da Imagem"
                        value={image.image_url}
                        onChange={e => handleImageChange(index, 'image_url', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Texto Alternativo"
                        value={image.alt_text}
                        onChange={e => handleImageChange(index, 'alt_text', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={image.is_primary}
                            onChange={e => handleImageChange(index, 'is_primary', e.target.checked)}
                            size="small"
                          />
                        }
                        label="Principal"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Ordem"
                        type="number"
                        value={image.display_order}
                        onChange={e => {
                          const newOrder = Number(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            images: prev.images.map((img, i) =>
                              i === index ? { ...img, display_order: newOrder } : img
                            ),
                          }));
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        onClick={() => handleImageDelete(index)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>

            {/* Specifications Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="h2">
                  Especificações do Produto
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addNewSpecification}
                  size="small"
                >
                  Adicionar Especificação
                </Button>
              </Box>

              {formData.specifications.map((spec, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Componente"
                        value={spec.key}
                        onChange={e => handleSpecificationChange(index, 'key', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Descrição"
                        value={spec.value}
                        onChange={e => handleSpecificationChange(index, 'value', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        onClick={() => handleSpecificationDelete(index)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/products')}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                  sx={{
                    backgroundColor: '#83b81a',
                    '&:hover': {
                      backgroundColor: '#5b8012',
                    },
                  }}
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Atualizando...'
                      : 'Salvando...'
                    : isEditMode
                      ? 'Atualizar Produto'
                      : 'Salvar Produto'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductFormPage;
